// Operator › Platform › Satellites (`/operator/satellites`; SATELLITE-REGISTRY, dispatcher design
// §4, §9): the hosts that run Hermes containers and the agents registered on each. One card per
// satellite — owner, status, transport, slots, last seen — with its actions as hover icons
// (check now, drain or undrain, add an agent, retire) and one row per agent endpoint (retire on
// hover). Adding and retiring happen in modals; the page stays put. A gateway key is typed once
// into the add-agent modal and never shown again by anything (the API seals it). Platform admins
// only (the API's 403 is shown as a notice).

import type { OperationOutput } from "@companyos/sdk";
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  InfoTip,
  Input,
  ListRow,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Switch,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ActivityIcon,
  ArchiveIcon,
  PauseIcon,
  PlayIcon,
  PlusIcon,
  ServerIcon,
  XIcon,
} from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { MeResponse } from "../../api/types";
import { currentWeek } from "../../focus/weeks";
import { formatDateTime } from "../format";
import { problemText } from "../ops-parts";
import { operatorDesQuery, operatorSystemAgentsQuery } from "../ops-queries";
import { OpsFrame, operatorCrumb } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";

type Satellite = OperationOutput<"operator.satellites.list">["data"][number];
type Endpoint = Satellite["endpoints"][number];
type Release = Satellite["releases"][number];

// SATELLITE-RUNTIME: a component's release on the host against the platform's pin.
const RELEASE_LABEL: Record<Release["component"], string> = {
  bridge: "Bridge",
  render_service: "Render",
  hermes_baseline: "Baseline",
};
const RELEASE_STATE: Record<
  Release["state"],
  { label: string; variant: "success" | "danger" | "warning" | "outline" | "neutral" }
> = {
  current: { label: "Current", variant: "success" },
  drift: { label: "Drift", variant: "danger" },
  behind: { label: "Behind", variant: "warning" },
  unreported: { label: "Not reported", variant: "outline" },
  unpinned: { label: "Not pinned", variant: "neutral" },
};

export const satellitesKey = ["operator", "satellites"] as const;
export const satellitesQuery = (api: Api) =>
  queryOptions({
    queryKey: satellitesKey,
    queryFn: () => api.client.operator.satellites.list(),
  });

const STATUS: Record<
  Satellite["status"],
  { label: string; variant: "success" | "danger" | "warning" | "outline" }
> = {
  live: { label: "Live", variant: "success" },
  unreachable: { label: "Unreachable", variant: "danger" },
  draining: { label: "Draining", variant: "warning" },
  retired: { label: "Retired", variant: "outline" },
};

/** The Hermes gateway's usual address on a satellite: its own port, else 8642. */
function defaultGateway(base: string): string {
  try {
    const u = new URL(base);
    if (!u.port) u.port = "8642";
    return u.origin;
  } catch {
    return base;
  }
}

/** Company names (the agency's grants) and agent names (DEs and system agents), by id. */
function useNames(api: Api, me: MeResponse) {
  const des = useQuery(operatorDesQuery(api, currentWeek()));
  const sys = useQuery(operatorSystemAgentsQuery(api));
  return useMemo(() => {
    const companies = new Map((me.agency?.grants ?? []).map((g) => [g.company_id, g.company_name]));
    const agents = new Map<string, { name: string; kind: "DE" | "System" }>();
    for (const r of des.data?.data ?? [])
      agents.set(r.digital_employee.membership_id, {
        name: r.digital_employee.display_name,
        kind: "DE",
      });
    for (const r of sys.data?.data ?? [])
      agents.set(r.membership_id, { name: r.name, kind: "System" });
    const agentsOf = (companyId: string) => [
      ...(des.data?.data ?? [])
        .filter((r) => r.company.id === companyId)
        .map((r) => ({
          id: r.digital_employee.membership_id,
          name: r.digital_employee.display_name,
          kind: "DE" as const,
        })),
      ...(sys.data?.data ?? [])
        .filter((r) => r.company.id === companyId && r.status === "active")
        .map((r) => ({ id: r.membership_id, name: r.name, kind: "System" as const })),
    ];
    return { companies, agents, agentsOf };
  }, [me, des.data, sys.data]);
}

type Names = ReturnType<typeof useNames>;

export function SatellitesPage({ api, me }: { api: Api; me: MeResponse }) {
  const satellites = useQuery(satellitesQuery(api));
  const names = useNames(api, me);
  const [adding, setAdding] = useState(false);
  const forbidden = satellites.error instanceof ApiError && satellites.error.status === 403;
  const rows = (satellites.data?.data ?? []).filter((s) => s.status !== "retired");
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Satellites" }]}
        icon={ServerIcon}
        title="Satellites"
        actions={
          forbidden ? null : (
            <Button size="sm" onClick={() => setAdding(true)} data-slot="add-satellite">
              <PlusIcon data-icon="inline-start" /> Satellite
            </Button>
          )
        }
      />
      <div className="grid gap-3 px-6 py-4 max-md:px-4">
        {satellites.isPending ? (
          <Loading label="Loading satellites" />
        ) : forbidden ? (
          <EmptyState
            variant="card"
            icon={ServerIcon}
            title="Platform admins only"
            description={problemText(
              satellites.error,
              "Satellites are managed by an admin of the platform's agency.",
            )}
          />
        ) : satellites.isError ? (
          <LoadError what="Satellites" error={satellites.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={ServerIcon}
            title="No satellites yet"
            description="Add the hosts that run your agents' Hermes containers."
          />
        ) : (
          <div className="grid gap-3" data-slot="satellites">
            {rows.map((s) => (
              <SatelliteCard key={s.id} api={api} me={me} s={s} names={names} />
            ))}
          </div>
        )}
      </div>
      {adding ? <AddSatelliteDialog api={api} me={me} onOpenChange={setAdding} /> : null}
    </OpsFrame>
  );
}

function SatelliteCard({
  api,
  me,
  s,
  names,
}: {
  api: Api;
  me: MeResponse;
  s: Satellite;
  names: Names;
}) {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: satellitesKey });
  const [retiring, setRetiring] = useState(false);
  const [addingAgent, setAddingAgent] = useState(false);
  const probe = useMutation({
    mutationFn: () => api.client.operator.satellites.probe({ params: { id: s.id } }),
    onSettled: refresh,
  });
  const drain = useMutation({
    mutationFn: (draining: boolean) =>
      api.client.operator.satellites.update({ params: { id: s.id }, body: { draining } }),
    onSettled: refresh,
  });
  const status = STATUS[s.status];
  const owner = s.company_id ? (names.companies.get(s.company_id) ?? "A company") : "Platform";
  return (
    <section
      className="group/sat rounded-lg border border-border bg-card"
      data-satellite={s.id}
      data-status={s.status}
      aria-label={s.name}
    >
      <div className="flex flex-wrap items-center gap-2 px-4 pt-3 pb-2">
        <h2 className="min-w-0 truncate text-sm font-semibold">{s.name}</h2>
        <Badge variant={status.variant}>{status.label}</Badge>
        <Badge variant="outline">{s.transport === "mtls" ? "mTLS" : "WireGuard"}</Badge>
        <Badge variant="neutral">{owner}</Badge>
        <div
          className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity group-hover/sat:opacity-100 group-focus-within/sat:opacity-100 pointer-coarse:opacity-100"
          data-slot="satellite-actions"
        >
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Check now"
            title="Check now"
            disabled={probe.isPending}
            onClick={() => probe.mutate()}
          >
            <ActivityIcon />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={s.status === "draining" ? "Undrain" : "Drain"}
            title={s.status === "draining" ? "Undrain: take new runs again" : "Drain: no new runs"}
            disabled={drain.isPending}
            onClick={() => drain.mutate(s.status !== "draining")}
          >
            {s.status === "draining" ? <PlayIcon /> : <PauseIcon />}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Add agent"
            title="Add agent"
            onClick={() => setAddingAgent(true)}
          >
            <PlusIcon />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Retire"
            title="Retire"
            onClick={() => setRetiring(true)}
          >
            <ArchiveIcon />
          </Button>
        </div>
      </div>
      <dl className="grid gap-x-4 gap-y-1 px-4 pb-3 text-xs sm:grid-cols-[7rem_1fr]">
        <dt className="text-muted-foreground">Address</dt>
        <dd className="min-w-0 break-all">
          <code>{s.base_address}</code>
        </dd>
        <dt className="flex items-center gap-1 text-muted-foreground">
          Slots
          <InfoTip label="About slots">
            How many runs this host takes at once. Runs beyond it wait in the queue.
          </InfoTip>
        </dt>
        <dd>
          {s.endpoints.length} agents · {s.max_concurrent_runs} slots
        </dd>
        <dt className="text-muted-foreground">Last seen</dt>
        <dd data-slot="last-seen">
          {s.last_seen_at ? formatDateTime(s.last_seen_at) : "Not yet"}
          {s.last_probe_detail ? (
            <span className="text-muted-foreground"> · {s.last_probe_detail}</span>
          ) : null}
        </dd>
        {s.releases.length > 0 ? (
          <>
            <dt className="flex items-center gap-1 text-muted-foreground">
              Releases
              <InfoTip label="About releases">
                What this host runs against the platform's pinned version. To roll back, pin the
                earlier release in the provisioning settings; the panel installs it.
              </InfoTip>
            </dt>
            <dd className="flex flex-wrap gap-1.5" data-slot="releases">
              {s.releases.map((r) => (
                <Badge
                  key={r.component}
                  variant={RELEASE_STATE[r.state].variant}
                  data-release={r.component}
                  data-state={r.state}
                  title={r.drift.length ? `Differs: ${r.drift.slice(0, 5).join(", ")}` : undefined}
                >
                  {RELEASE_LABEL[r.component]} {r.installed?.version ?? "—"}
                  {r.pinned && r.installed?.version !== r.pinned.version
                    ? ` → ${r.pinned.version}`
                    : ""}{" "}
                  · {RELEASE_STATE[r.state].label}
                </Badge>
              ))}
            </dd>
          </>
        ) : null}
      </dl>
      {probe.error || drain.error ? (
        <p className="px-4 pb-2 text-xs text-danger-text">
          {problemText(probe.error ?? drain.error, "That did not work.")}
        </p>
      ) : null}
      {s.endpoints.length > 0 ? (
        <div className="border-t border-subtle" data-slot="endpoints">
          {s.endpoints.map((e) => (
            <EndpointRow key={e.id} api={api} e={e} names={names} onDone={refresh} />
          ))}
        </div>
      ) : null}
      {retiring ? (
        <RetireDialog
          title={`Retire ${s.name}?`}
          description={`Its ${s.endpoints.length} agents stop running here and need a new home. This cannot be undone.`}
          onOpenChange={setRetiring}
          retire={(reason) =>
            api.client.operator.satellites.retire({ params: { id: s.id }, body: { reason } })
          }
          onDone={refresh}
        />
      ) : null}
      {addingAgent ? (
        <AddEndpointDialog
          api={api}
          me={me}
          satellite={s}
          names={names}
          onOpenChange={setAddingAgent}
        />
      ) : null}
    </section>
  );
}

function EndpointRow({
  api,
  e,
  names,
  onDone,
}: {
  api: Api;
  e: Endpoint;
  names: Names;
  onDone: () => void;
}) {
  const [retiring, setRetiring] = useState(false);
  const agent = names.agents.get(e.de_membership_id);
  const company = names.companies.get(e.company_id) ?? "A company";
  return (
    <>
      <ListRow
        data-endpoint={e.id}
        title={agent?.name ?? "An agent"}
        chips={
          <Badge variant={agent?.kind === "System" ? "info" : "accent"}>
            {agent?.kind ?? "DE"}
          </Badge>
        }
        meta={
          <span>
            {company} · {e.instance_id}/{e.profile_name}
            {" · "}
            {!e.probe
              ? "key not checked yet"
              : !e.probe.current
                ? "key changed since the last check"
                : e.probe.ok
                  ? "key accepted"
                  : (e.probe.detail ?? "key check failed")}
          </span>
        }
        actions={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Retire endpoint"
            title="Retire endpoint"
            onClick={() => setRetiring(true)}
          >
            <XIcon />
          </Button>
        }
      />
      {retiring ? (
        <RetireDialog
          title={`Retire ${agent?.name ?? "this agent"}'s endpoint?`}
          description="The agent stops running on this host until it is registered again."
          onOpenChange={setRetiring}
          retire={(reason) =>
            api.client.operator.agentEndpoints.retire({ params: { id: e.id }, body: { reason } })
          }
          onDone={onDone}
        />
      ) : null}
    </>
  );
}

function RetireDialog({
  title,
  description,
  onOpenChange,
  retire,
  onDone,
}: {
  title: string;
  description: string;
  onOpenChange: (open: boolean) => void;
  retire: (reason: string) => Promise<unknown>;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const id = useId();
  const m = useMutation({
    mutationFn: () => retire(reason.trim()),
    onSuccess: () => {
      onDone();
      onOpenChange(false);
    },
  });
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (reason.trim()) m.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={id}>Reason</FieldLabel>
            <Input
              id={id}
              value={reason}
              onChange={(ev) => setReason(ev.target.value)}
              autoComplete="off"
            />
            {m.error ? (
              <FieldError>{problemText(m.error, "It could not be retired.")}</FieldError>
            ) : null}
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" variant="destructive" disabled={m.isPending || !reason.trim()}>
              {m.isPending ? "Retiring…" : "Retire"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddSatelliteDialog({
  api,
  me,
  onOpenChange,
}: {
  api: Api;
  me: MeResponse;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [owner, setOwner] = useState("");
  const [transport, setTransport] = useState<"wireguard" | "mtls">("wireguard");
  const [address, setAddress] = useState("");
  const [slots, setSlots] = useState("4");
  const ids = {
    name: useId(),
    owner: useId(),
    transport: useId(),
    address: useId(),
    slots: useId(),
  };
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.satellites.create({
        body: {
          name: name.trim(),
          ...(owner ? { company_id: owner } : {}),
          transport,
          base_address: address.trim(),
          max_concurrent_runs: Math.max(1, Math.min(1000, Number(slots) || 4)),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: satellitesKey });
      onOpenChange(false);
    },
  });
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (name.trim() && address.trim()) save.mutate();
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="grid gap-4" data-slot="add-satellite-form">
          <DialogHeader>
            <DialogTitle>Add satellite</DialogTitle>
            <DialogDescription>A host that runs Hermes containers for one owner.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
            <Input
              id={ids.name}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.owner}>Owner</FieldLabel>
            <NativeSelect id={ids.owner} value={owner} onChange={(e) => setOwner(e.target.value)}>
              <NativeSelectOption value="">Platform (shared capacity)</NativeSelectOption>
              {(me.agency?.grants ?? []).map((g) => (
                <NativeSelectOption key={g.company_id} value={g.company_id}>
                  {g.company_name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <div className="flex items-center gap-1">
              <FieldLabel htmlFor={ids.transport}>Transport</FieldLabel>
              <InfoTip label="About transport">
                WireGuard: the private overlay. mTLS: https over the internet with a CompanyOS
                client certificate (issued per satellite).
              </InfoTip>
            </div>
            <NativeSelect
              id={ids.transport}
              value={transport}
              onChange={(e) => setTransport(e.target.value as "wireguard" | "mtls")}
            >
              <NativeSelectOption value="wireguard">WireGuard</NativeSelectOption>
              <NativeSelectOption value="mtls">mTLS</NativeSelectOption>
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.address}>Address</FieldLabel>
            <Input
              id={ids.address}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={transport === "mtls" ? "https://sat.example.com" : "http://100.64.0.10"}
              autoComplete="off"
              spellCheck={false}
            />
            <FieldDescription>Scheme, host and port only.</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.slots}>Slots</FieldLabel>
            <Input
              id={ids.slots}
              type="number"
              min={1}
              max={1000}
              value={slots}
              onChange={(e) => setSlots(e.target.value)}
            />
          </Field>
          {save.error ? (
            <FieldError>{problemText(save.error, "The satellite could not be added.")}</FieldError>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={save.isPending || !name.trim() || !address.trim()}>
              {save.isPending ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddEndpointDialog({
  api,
  me,
  satellite,
  names,
  onOpenChange,
}: {
  api: Api;
  me: MeResponse;
  satellite: Satellite;
  names: Names;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const companies = satellite.company_id
    ? (me.agency?.grants ?? []).filter((g) => g.company_id === satellite.company_id)
    : (me.agency?.grants ?? []);
  const [companyId, setCompanyId] = useState(companies[0]?.company_id ?? "");
  const agents = names.agentsOf(companyId);
  const [agentId, setAgentId] = useState("");
  const [instance, setInstance] = useState("");
  const [profile, setProfile] = useState("");
  const [url, setUrl] = useState(() => defaultGateway(satellite.base_address));
  const [multiplex, setMultiplex] = useState(true);
  const [key, setKey] = useState("");
  const ids = {
    company: useId(),
    agent: useId(),
    instance: useId(),
    profile: useId(),
    url: useId(),
    key: useId(),
    multiplex: useId(),
  };
  const chosen = agentId || agents[0]?.id || "";
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.agentEndpoints.create({
        body: {
          company_id: companyId,
          de_membership_id: chosen,
          satellite_id: satellite.id,
          instance_id: instance.trim(),
          profile_name: profile.trim(),
          api_base_url: url.trim(),
          multiplex,
          api_key: key,
        },
      }),
    onSuccess: () => {
      setKey("");
      void qc.invalidateQueries({ queryKey: satellitesKey });
      onOpenChange(false);
    },
  });
  const ready =
    companyId && chosen && instance.trim() && profile.trim() && url.trim() && key.length >= 16;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form
          className="grid gap-4"
          data-slot="add-endpoint-form"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (ready) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Add agent to {satellite.name}</DialogTitle>
            <DialogDescription>
              Where the agent's Hermes profile answers on this host.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.company}>Company</FieldLabel>
            <NativeSelect
              id={ids.company}
              value={companyId}
              onChange={(e) => {
                setCompanyId(e.target.value);
                setAgentId("");
              }}
            >
              {companies.map((g) => (
                <NativeSelectOption key={g.company_id} value={g.company_id}>
                  {g.company_name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.agent}>Agent</FieldLabel>
            <NativeSelect
              id={ids.agent}
              value={chosen}
              onChange={(e) => setAgentId(e.target.value)}
            >
              {agents.map((a) => (
                <NativeSelectOption key={a.id} value={a.id}>
                  {a.name} ({a.kind})
                </NativeSelectOption>
              ))}
            </NativeSelect>
            {agents.length === 0 ? (
              <FieldDescription>No agents in this company yet.</FieldDescription>
            ) : null}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor={ids.instance}>Container</FieldLabel>
              <Input
                id={ids.instance}
                value={instance}
                onChange={(e) => setInstance(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.profile}>Profile</FieldLabel>
              <Input
                id={ids.profile}
                value={profile}
                onChange={(e) => setProfile(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor={ids.url}>Gateway URL</FieldLabel>
            <Input
              id={ids.url}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
          <div className="flex items-center gap-2 text-sm">
            <Switch id={ids.multiplex} checked={multiplex} onCheckedChange={setMultiplex} />
            <FieldLabel htmlFor={ids.multiplex}>Profiles share the container</FieldLabel>
            <InfoTip label="About shared containers">
              On: the gateway serves this profile at /p/&lt;profile&gt; with the profile's own key.
              Off: the container is this one agent.
            </InfoTip>
          </div>
          <Field>
            <div className="flex items-center gap-1">
              <FieldLabel htmlFor={ids.key}>Gateway key</FieldLabel>
              <InfoTip label="About the gateway key">
                The profile's API_SERVER_KEY. Sealed on save and never shown again, here or
                anywhere.
              </InfoTip>
            </div>
            <Input
              id={ids.key}
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              autoComplete="new-password"
              spellCheck={false}
            />
          </Field>
          {save.error ? (
            <FieldError>{problemText(save.error, "The agent could not be added.")}</FieldError>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={save.isPending || !ready}>
              {save.isPending ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
